// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FeaturedAthlete {
  id: string;
  profile_id: string;
  score: number | null;
  profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    avatar_url: string | null;
    primary_sport_id: string | null;
  };
}

export interface TrendingHashtag {
  id: string;
  tag: string;
  post_count: number | null;
}

export interface FeaturedLane {
  id: string;
  name: string;
  description: string | null;
  emoji: string;
  reaction_type: string;
  sort_order: number | null;
}

export interface DraftPick {
  id: string;
  pick_number: number;
  total_reactions: number;
  reaction_variety: number;
  post: {
    id: string;
    profile_id: string;
    content: string | null;
    profile: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      username: string | null;
      avatar_url: string | null;
    };
  };
}

export function useFeaturedAthletes() {
  return useQuery({
    queryKey: ['featured-athletes'],
    queryFn: async () => {
      // First fetch featured athletes
      const { data: featuredData, error: featuredError } = await supabase
        .from('wall_featured_athletes')
        .select('id, profile_id, score')
        .order('score', { ascending: false })
        .limit(10);

      if (featuredError) throw featuredError;
      if (!featuredData || featuredData.length === 0) return [];

      // Get unique profile IDs
      const profileIds = [...new Set(featuredData.map(f => f.profile_id))];

      // Fetch profiles separately
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, username, avatar_url, primary_sport_id')
        .in('id', profileIds);

      if (profilesError) throw profilesError;

      // Create profile lookup map
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Combine data
      return featuredData.map((item) => ({
        id: item.id,
        profile_id: item.profile_id,
        score: item.score,
        profile: profileMap.get(item.profile_id) || {
          id: item.profile_id,
          first_name: null,
          last_name: null,
          username: null,
          avatar_url: null,
          primary_sport_id: null,
        },
      })) as FeaturedAthlete[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useTrendingHashtags() {
  return useQuery({
    queryKey: ['trending-hashtags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wall_hashtags')
        .select('id, tag, post_count')
        .order('post_count', { ascending: false })
        .limit(15);

      if (error) throw error;
      return data as TrendingHashtag[];
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useFeaturedLanes() {
  return useQuery({
    queryKey: ['featured-lanes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wall_featured_lanes')
        .select('id, name, description, emoji, reaction_type, sort_order')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data as FeaturedLane[];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useDraftBoard() {
  return useQuery({
    queryKey: ['draft-board'],
    queryFn: async () => {
      // Get the latest published draft board
      const { data: board, error: boardError } = await supabase
        .from('wall_draft_boards')
        .select('id')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (boardError) throw boardError;
      if (!board) return [];

      // Get picks for this board
      const { data: picks, error: picksError } = await supabase
        .from('wall_draft_picks')
        .select(`
          id,
          pick_number,
          total_reactions,
          reaction_variety,
          wall_posts!wall_draft_picks_post_id_fkey (
            id,
            profile_id,
            content,
            profiles!wall_posts_profile_id_fkey (
              id,
              first_name,
              last_name,
              username,
              avatar_url
            )
          )
        `)
        .eq('draft_board_id', board.id)
        .order('pick_number')
        .limit(10);

      if (picksError) throw picksError;

      return (picks || []).map((pick: any) => ({
        ...pick,
        post: {
          ...pick.wall_posts,
          profile: pick.wall_posts?.profiles,
        },
      })) as DraftPick[];
    },
    staleTime: 1000 * 60 * 5,
  });
}
